import React, { useState, useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import PropTypes from 'prop-types';
import api from '@services/api';
import FilterableSelect from '@components/common/FilterableSelect';
import { useModels } from '@hooks/useModels';
import './MetadataForm.scss';

/**
 * User select component for metadata forms
 */
const UserSelect = ({ key_name, value, onChange, disabled }) => {
  const { t } = useLanguage();

  // Use the useModels hook to fetch users with pagination -1 (get all)
  const { models: users, loading, error, refetch } = useModels('users', {
    endpoint: 'getAll'
  });

  // Fetch all users when component mounts
  useEffect(() => {
    refetch({
      page: 1,
      page_size: -1, // Get all users
      is_active: true // Only active users
    });
  }, [refetch]);

  if (loading) {
    return (
      <div className="metadata-input metadata-input--loading">
        <span>{t('common.loading') || 'Loading...'}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metadata-input metadata-input--error">
        <span>{t('common.error') || 'Error'}: {error}</span>
      </div>
    );
  }

  return (
    <FilterableSelect
      options={users}
      value={value}
      onChange={(selectedValue) => onChange(key_name, selectedValue)}
      placeholder={t('metadata.selectUser') || 'Select a user...'}
      searchPlaceholder={t('metadata.searchUsers') || 'Search users...'}
      className="metadata-input metadata-input--user"
      disabled={disabled}
      clearable={true}
      getOptionLabel={(user) => user.name || user.email}
      getOptionValue={(user) => String(user.id)}
      noOptionsText={t('metadata.noUsersFound') || 'No users found'}
      renderOption={(user, isSelected) => (
        <div className={`user-option ${isSelected ? 'user-option--selected' : ''}`}>
          <div className="user-option__info">
            <span className="user-option__name">
              {user.name || user.email}
            </span>
            {user.name && (
              <span className="user-option__email">{user.email}</span>
            )}
          </div>
          {isSelected && (
            <span className="user-option__check">✓</span>
          )}
        </div>
      )}
    />
  );
};

/**
 * Dynamic metadata form component that renders different input types
 * based on metadata definitions and manages resource metadata
 *
 * @param {number} resourceId - ID of the resource (optional for new resources)
 * @param {Array} existingMetadata - Array of existing metadata objects
 * @param {boolean} isConsumable - Whether the resource is consumable (from resource.consumable property)
 * @param {Function} onChange - Callback function called when metadata changes
 * @param {boolean} disabled - Whether the form should be disabled

 */
const MetadataForm = ({
  resourceId = null,
  existingMetadata = [],
  isConsumable = false,
  onChange,
  disabled = false,
}) => {
  const { t } = useLanguage();
  const [metadataDefinitions, setMetadataDefinitions] = useState([]);
  const [metadataValues, setMetadataValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize metadata values from existing metadata
  useEffect(() => {
    if (existingMetadata && existingMetadata.length > 0) {
      const initialValues = {};
      existingMetadata.forEach(meta => {
        initialValues[meta.metadata_key] = meta.value;
      });
      setMetadataValues(initialValues);
    }
  }, [existingMetadata]);

  // Fetch metadata definitions based on resource type
  useEffect(() => {
    const fetchMetadataDefinitions = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          page: 1,
          page_size: -1, // Get all definitions
        };

        const response = await api.metadata.getDefinitions(params);
        const definitions = response.data.models || [];

        // Filter definitions based on resource type
        let filteredDefinitions;

          filteredDefinitions = definitions.filter(def => {
            // Exclude allows_participation and reservation_managers as they're managed separately
            if (def.key_name === 'allows_participation' || def.key_name === 'reservation_managers') {
              return false;
            }
            const shouldShow = isConsumable ? def.applies_to_consumable : def.applies_to_non_consumable;
            return shouldShow;
          });

        setMetadataDefinitions(filteredDefinitions);
      } catch (err) {
        setError(err.message || 'Failed to fetch metadata definitions');
      } finally {
        setLoading(false);
      }
    };

    fetchMetadataDefinitions();
  }, [isConsumable]);

  // Handle metadata value changes
  const handleMetadataChange = (key, value) => {
    const newValues = {
      ...metadataValues,
      [key]: value
    };

    setMetadataValues(newValues);

    // Convert to array format for parent component
    // Include all metadata values, even empty ones, so MetadataManager can handle deletions
    const metadataArray = Object.entries(newValues)
      .map(([metadataKey, metadataValue]) => ({
        metadata_key: metadataKey,
        value: metadataValue === null || metadataValue === undefined ? '' : String(metadataValue)
      }));

    // Call parent onChange
    if (onChange) {
      onChange(metadataArray);
    }
  };

  // Handle metadata removal
  const handleRemoveMetadata = (key) => {
    const newValues = { ...metadataValues };
    // Set to empty string to signal removal
    newValues[key] = '';
    setMetadataValues(newValues);

    // Update parent - include the empty value so MetadataManager can handle deletion
    const metadataArray = Object.entries(newValues)
      .map(([metadataKey, metadataValue]) => ({
        metadata_key: metadataKey,
        value: metadataValue === null || metadataValue === undefined ? '' : String(metadataValue)
      }));

    if (onChange) {
      onChange(metadataArray);
    }
  };

  // Render input based on data type
  const renderInput = (definition) => {
    const { key_name, data_type, is_required } = definition;
    const value = metadataValues[key_name] || '';

    const commonProps = {
      id: `metadata-${key_name}`,
      name: key_name,
      disabled: disabled,
      required: is_required,
      'aria-describedby': `${key_name}-description`
    };

    switch (data_type) {
      case 'string':
        return (
          <input
            type="text"
            {...commonProps}
            value={value}
            onChange={(e) => handleMetadataChange(key_name, e.target.value)}
            placeholder={`${t('metadata.enterValue') || 'Enter value'}...`}
            className="metadata-input metadata-input--text"
          />
        );

      case 'integer':
        return (
          <input
            type="number"
            {...commonProps}
            value={value}
            onChange={(e) => handleMetadataChange(key_name, e.target.value)}
            placeholder="0"
            className="metadata-input metadata-input--number"
            step="1"
          />
        );

      case 'decimal':
        return (
          <input
            type="number"
            {...commonProps}
            value={value}
            onChange={(e) => handleMetadataChange(key_name, e.target.value)}
            placeholder="0.00"
            className="metadata-input metadata-input--decimal"
            step="0.01"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            {...commonProps}
            value={value}
            onChange={(e) => handleMetadataChange(key_name, e.target.value)}
            className="metadata-input metadata-input--date"
          />
        );

      case 'boolean':
        return (
          <div className="metadata-boolean-group">
            <label className="metadata-boolean-option">
              <input
                type="radio"
                {...commonProps}
                value="true"
                checked={value === 'true' || value === true}
                onChange={() => handleMetadataChange(key_name, 'true')}
              />
              <span>{t('common.yes') || 'Oui'}</span>
            </label>
            <label className="metadata-boolean-option">
              <input
                type="radio"
                {...commonProps}
                value="false"
                checked={value === 'false' || value === false}
                onChange={() => handleMetadataChange(key_name, 'false')}
              />
              <span>{t('common.no') || 'Non'}</span>
            </label>
            <label className="metadata-boolean-option">
              <input
                type="radio"
                {...commonProps}
                value=""
                checked={!value || value === ''}
                onChange={() => handleMetadataChange(key_name, '')}
              />
              <span>{t('common.notSet') || 'Non défini'}</span>
            </label>
          </div>
        );

      case 'user':
        return <UserSelect key_name={key_name} value={value} onChange={handleMetadataChange} disabled={disabled} />;

      default:
        return (
          <input
            type="text"
            {...commonProps}
            value={value}
            onChange={(e) => handleMetadataChange(key_name, e.target.value)}
            placeholder={`${t('metadata.enterValue') || 'Enter value'}...`}
            className="metadata-input metadata-input--text"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="metadata-form metadata-form--loading">
        <div className="metadata-form__spinner">
          <div className="spinner"></div>
          <span>{t('metadata.loadingDefinitions') || 'Loading metadata definitions...'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metadata-form metadata-form--error">
        <div className="metadata-form__error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (metadataDefinitions.length === 0) {
    return (
      <div className="metadata-form metadata-form--empty">
        <p className="metadata-form__empty-message">
          {t('metadata.noDefinitionsAvailable') || 'No metadata definitions available for this resource type.'}
        </p>
      </div>
    );
  }

  // Separate required and optional metadata
  const requiredDefinitions = metadataDefinitions.filter(def => def.is_required);
  const optionalDefinitions = metadataDefinitions.filter(def => !def.is_required);

  return (
    <div className="form-group">
        <label className="form-group__label">
            Metadata
        </label>
        <div className="metadata-form">
        <div className="metadata-form__sections">
            {/* Required Metadata */}
            {requiredDefinitions.length > 0 && (
            <div className="metadata-section metadata-section--required">
                <div className="metadata-section__fields">
                {requiredDefinitions.map(definition => (
                    <div key={definition.key_name} className="metadata-field metadata-field--required">
                    <label
                        htmlFor={`metadata-${definition.key_name}`}
                        className="metadata-field__label"
                    >
                        {t(`metadata.fields.${definition.key_name}.label`)}
                        <span className="metadata-field__required">*</span>
                    </label>

                    {renderInput(definition)}

                    <p
                        id={`${definition.key_name}-description`}
                        className="metadata-field__description"
                    >
                        {t(`metadata.fields.${definition.key_name}.description`)}
                    </p>
                    </div>
                ))}
                </div>
            </div>
            )}

            {/* Optional Metadata */}
            {optionalDefinitions.length > 0 && (
            <div className="metadata-section metadata-section--optional">
                <div className="metadata-section__fields">
                {optionalDefinitions.map(definition => {
                    const hasValue = metadataValues[definition.key_name] !== undefined &&
                                    metadataValues[definition.key_name] !== '' &&
                                    metadataValues[definition.key_name] !== null;

                    return (
                    <div key={definition.key_name} className="metadata-field metadata-field--optional">
                        <div className="metadata-field__header">
                        <label
                            htmlFor={`metadata-${definition.key_name}`}
                            className="metadata-field__label"
                        >
                            {t(`metadata.fields.${definition.key_name}.label`)}
                        </label>

                        {hasValue && (
                            <button
                            type="button"
                            className="metadata-field__remove"
                            onClick={() => handleRemoveMetadata(definition.key_name)}
                            title={t('metadata.removeField') || 'Remove field'}
                            disabled={disabled}
                            >
                            ×
                            </button>
                        )}
                        </div>

                        {renderInput(definition)}

                        <p
                        id={`${definition.key_name}-description`}
                        className="metadata-field__description"
                        >
                        {t(`metadata.fields.${definition.key_name}.description`)}
                        </p>
                    </div>
                    );
                })}
                </div>
            </div>
            )}
        </div>
        </div>
    </div>
  );
};

MetadataForm.propTypes = {
  resourceId: PropTypes.number,
  existingMetadata: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    metadata_key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    resource_id: PropTypes.number
  })),
  // Should be set to resource.consumable from the resource model
  isConsumable: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default MetadataForm;
